<template>
  <div class="flex flex-col h-screen bg-gray-100">
    <!-- Custom Header -->
    <SelectHeader />

    <!-- Filter Bar -->
    <div class="bg-white px-1.5 py-1 border-b">
      <div class="flex items-center gap-1.5">
        <!-- 브랜드 선택 -->
        <div class="flex-1 relative">
          <div class="relative">
            <select v-model="selectedBrand"
              class="appearance-none w-full px-2 py-1 pr-6 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white">
              <option value="">브랜드</option>
              <option v-for="brand in brands" :key="brand.value" :value="brand.value">
                {{ brand.label }}
              </option>
            </select>
            <div class="absolute inset-y-0 right-0 flex items-center px-1.5 pointer-events-none">
              <ChevronDownIcon size="12" class="text-gray-400" />
            </div>
          </div>
        </div>

        <!-- 연도 선택 -->
        <div class="flex-1 relative">
          <div class="relative">
            <select v-model="selectedYear"
              class="appearance-none w-full px-2 py-1 pr-6 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white">
              <option value="">연도</option>
              <option v-for="year in years" :key="year.value" :value="year.value">
                {{ year.label }}
              </option>
            </select>
            <div class="absolute inset-y-0 right-0 flex items-center px-1.5 pointer-events-none">
              <ChevronDownIcon size="12" class="text-gray-400" />
            </div>
          </div>
        </div>

        <!-- 시즌 선택 -->
        <div class="flex-1 relative">
          <div class="relative">
            <select v-model="selectedSeason"
              class="appearance-none w-full px-2 py-1 pr-6 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white">
              <option value="">시즌</option>
              <option v-for="season in seasons" :key="season.value" :value="season.value">
                {{ season.label }}
              </option>
            </select>
            <div class="absolute inset-y-0 right-0 flex items-center px-1.5 pointer-events-none">
              <ChevronDownIcon size="12" class="text-gray-400" />
            </div>
          </div>
        </div>

        <!-- 조회 버튼 -->
        <button @click="handleSearch"
          class="px-3 py-1.5 bg-indigo-500 text-white text-xs sm:text-sm rounded-md hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors">
          조회
        </button>
      </div>
    </div>

    <!-- Summary Bar -->
    <div class="bg-white px-2 sm:px-6 py-2 sm:py-4 border-b">
      <!-- 기존 요약 정보 -->
      <div class="flex items-center justify-between text-sm sm:text-base text-gray-700 gap-2 sm:gap-4">
        <div>
          입력 DP수: <span class="font-semibold">{{products.filter(p => hasQuantityInput(p)).length}}</span>
          / <span class="font-semibold">{{ products.length }}</span>
        </div>
        <div>
          총 수량: <span class="font-semibold">{{ totalQuantity }}</span>개
        </div>
        <div>
          총 금액: <span class="font-semibold">{{ totalAmount.toLocaleString() }}원</span>
        </div>
      </div>

      <!-- 아이템 타입별 요약 수정 -->
      <div class="mt-2 pt-2 border-t">
        <div class="grid grid-cols-2 text-sm sm:text-base text-gray-700">
          <div class="text-left pl-4">
            유아복: <span class="font-semibold ml-1">
              {{Number(itemSummary.find(item => item.IT === '2')?.AMT || 0).toLocaleString()}}원
            </span>
          </div>
          <div class="text-left pl-4">
            토들러: <span class="font-semibold ml-1">
              {{Number(itemSummary.find(item => item.IT === '3')?.AMT || 0).toLocaleString()}}원
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex flex-1 overflow-hidden">
      <!-- Left side: Search and Product Cards -->
      <div class="w-1/3 min-w-[150px] flex flex-col bg-white shadow-lg">
        <!-- Search Bar -->
        <div class="p-1 border-b">
          <div class="relative">
            <input type="text" v-model="searchQuery" placeholder="검색..."
              class="w-full px-2 py-1 pl-6 pr-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500">
            <SearchIcon class="absolute left-1.5 top-1.5 h-3.5 w-3.5 text-gray-400" />
          </div>
        </div>

        <!-- Product List -->
        <div class="flex-1 overflow-y-auto p-2 sm:p-3">
          <div class="grid grid-cols-1 gap-2 sm:gap-3">
            <div v-for="product in filteredProducts" :key="product.id" class="rounded-lg shadow-sm p-2 cursor-pointer relative text-sm
              transition-all duration-150" :class="{
                'ring-2 ring-indigo-500': selectedProduct === product,
                'bg-indigo-50': hasQuantityInput(product),
                'hover:shadow hover:bg-gray-50': !hasQuantityInput(product),
                'hover:bg-indigo-100': hasQuantityInput(product)
              }" @click="selectProduct(product)">
              <div class="flex items-center justify-between">
                <div class="flex flex-col gap-1">
                  <!-- DP 코드와 컬러 뱃지 -->
                  <div class="flex items-center">
                    <h3 class="text-sm sm:text-base font-medium text-gray-800">{{ product.DP_CD }}</h3>
                    <span :class="['color-badge', `badge-${product.COL_NM?.toLowerCase()}`]">
                      {{ product.COL_NM }}
                    </span>
                  </div>

                  <!-- 스타일명 -->
                  <span class="text-sm sm:text-base text-gray-600">{{ product.STY_NM }}</span>

                  <!-- 가격 -->
                  <div class="flex items-center gap-1 text-gray-600 truncate">
                    <span class="text-sm sm:text-base">₩{{ Number(product.TAG_PRCE).toLocaleString() }}</span>
                  </div>

                  <!-- 입력 수량 -->
                  <p class="text-xs sm:text-sm" :class="{
                    'text-gray-600': !hasQuantityInput(product),
                    'text-indigo-600 font-medium hover:text-indigo-800': hasQuantityInput(product)
                  }">
                    입력 수량: <span class="font-medium">{{ product.QTY_SUM }}</span>
                  </p>
                </div>
              </div>

              <!-- 평가 아이콘 (아래로 이동) -->
              <div v-if="product.VOTE" class="absolute bottom-1.5 sm:bottom-2 right-1.5 sm:right-2">
                <span v-if="product.VOTE == '5'" class="text-green-500">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 sm:h-6 sm:w-6 " fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                      d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                  </svg>
                </span>
                <span v-else-if="product.VOTE == '1'" class="text-red-500 ">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                      d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right side: Detailed View -->
      <div class="w-2/3 overflow-y-auto bg-gray-50 p-1 sm:p-2" v-if="selectedProduct">
        <!-- Header with Name -->
        <div class="flex flex-col items-center justify-center mb-2 sm:mb-3 gap-1">
          <div class="flex items-center">
            <h2 class="text-base sm:text-lg font-bold text-gray-800">{{ selectedProduct.DP_CD }}</h2>
            <span :class="['color-badge', `badge-${selectedProduct.COL_NM?.toLowerCase()}`]">
              {{ selectedProduct.COL_NM }}
            </span>
          </div>
          <h3 class="text-sm sm:text-base text-gray-600">{{ selectedProduct.STY_NM }}</h3>
        </div>

        <!-- Product Info Summary -->
        <div class="bg-white rounded-lg shadow-sm mb-2 sm:mb-3">
          <div class="px-1.5 sm:px-2">
            <div class="flex flex-col">
              <div class="flex items-center justify-between py-1.5 sm:py-2">
                <div class="text-sm sm:text-base text-gray-500">총 주문수량</div>
                <div class="text-sm sm:text-base font-medium text-gray-500 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full">
                  {{ calculateTotalQuantity(selectedProduct) }}개
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Size and Quantity Inputs -->
        <div class="bg-white rounded-lg shadow-sm mb-2 sm:mb-3">
          <div class="p-2 sm:p-3">
            <h3 class="text-base sm:text-lg font-medium text-gray-800">수량 입력 <span
                class="text-sm sm:text-base text-gray-500">(최대 15개)</span></h3>
          </div>
          <div class="px-2 sm:px-3 pb-2 sm:pb-3">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2">
              <div v-for="size in selectedProduct.sizes" :key="size"
                class="flex items-center justify-between bg-gray-50 p-1.5 sm:p-2 rounded-md">
                <label :for="size" class="text-sm sm:text-base text-gray-600">
                  {{ selectedProduct.sizeNames[size] }}
                </label>
                <div class="flex items-center gap-0.5 sm:gap-1">
                  <button @click.prevent="updateQuantity(size, (selectedProduct.sizeQuantities[size] || 0) - 1)"
                    class="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors touch-manipulation"
                    :disabled="!selectedProduct.sizeQuantities[size]">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-4 sm:w-4" viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
                    </svg>
                  </button>
                  <input :id="size" type="number" :value="selectedProduct.sizeQuantities[size]" @input="e => {
                    e.target.value = e.target.value > 15 ? 15 : e.target.value;
                    updateQuantity(size, e.target.value);
                  }" min="0" max="15"
                    class="w-8 sm:w-10 text-center border-0 bg-transparent text-sm sm:text-base font-medium focus:outline-none focus:ring-0"
                    :class="{
                      'text-indigo-600': selectedProduct.sizeQuantities[size],
                      'text-red-500': selectedProduct.sizeQuantities[size] > 15
                    }" inputmode="numeric" pattern="[0-9]*">
                  <button @click.prevent="updateQuantity(size, (selectedProduct.sizeQuantities[size] || 0) + 1)"
                    class="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors touch-manipulation"
                    :disabled="selectedProduct.sizeQuantities[size] >= 15">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-4 sm:w-4" viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Evaluation -->
        <div class="bg-white rounded-lg shadow-sm">
          <div class="p-2 sm:p-3 space-y-2.5 sm:space-y-4">
            <!-- Rating Buttons -->
            <div class="flex gap-1.5 sm:gap-2">
              <button @click="updateVote('5')"
                class="flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-1.5 sm:py-2 px-2 sm:px-3 rounded-md border text-sm sm:text-base transition-colors"
                :class="[
                  selectedProduct.VOTE == '5'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                ]">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                </svg>
                <span>좋음</span>
              </button>
              <button @click="updateVote('1')"
                class="flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-1.5 sm:py-2 px-2 sm:px-3 rounded-md border text-sm sm:text-base transition-colors"
                :class="[
                  selectedProduct.VOTE == '1'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-200 hover:border-red-300 hover:bg-red-50'
                ]">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                </svg>
                <span>나쁨</span>
              </button>
            </div>

            <!-- Evaluation Reason -->
            <div class="mt-2 sm:mt-3">
              <!-- <label for="evaluationReason" class="block text-sm sm:text-base font-medium text-gray-700 mb-1">평가
                사유</label> -->
              <textarea id="evaluationReason" v-model="selectedProduct.evaluationReason"
                @input="updateEvaluationReason($event.target.value)" rows="3"
                class="w-full px-2 py-1 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="평가 사유를 입력하세요..."></textarea>
            </div>
          </div>
        </div>

        <!-- Save Button -->
        <div class="sticky bottom-0 bg-white border-t shadow-lg p-2 sm:p-3">
          <button @click="handleSave" class="w-full bg-indigo-600 text-white text-sm sm:text-base px-3 sm:px-4 py-2 sm:py-2.5 rounded-md
              hover:bg-indigo-700 transition-colors
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
              disabled:opacity-50 disabled:cursor-not-allowed" :disabled="loading">
            저장하기
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'
import { SearchIcon, ChevronDownIcon } from 'lucide-vue-next'
import SelectHeader from '@/components/select/SelectHeader.vue'
import { useAuthStore } from '@/stores/auth'
import dpAPI from '@/api/dp/dp.js'
import { debounce } from 'lodash'

const { proxy } = getCurrentInstance()

const authStore = useAuthStore()

// 상태 관리
const selectedProduct = ref(null)
const products = ref([])
const searchQuery = ref('')
const selectedBrand = ref('01')
const selectedYear = ref('S')
const selectedSeason = ref('7')
const loading = ref(false)  // loading ref를 최상단에서 초기화

const brands = ref([])
const years = ref([])
const seasons = ref([])

// 아이템 타입별 요약 데이터를 위한 ref 추가
const itemSummary = ref([])


// 총 수량 계산 (모든 상품의 QTY_SUM 합계)
const totalQuantity = computed(() => {
  return products.value.reduce((sum, product) => sum + (Number(product.QTY_SUM) || 0), 0);
});

// 총 금액 계산 (수량 * 단가)
const totalAmount = computed(() => {
  return products.value.reduce((sum, product) => {
    const quantity = Number(product.QTY_SUM) || 0;
    const price = Number(product.TAG_PRCE) || 0;
    return sum + (quantity * price);
  }, 0);
});

const showSuccess = (message) => {
  proxy.$swal.fire({
    icon: 'success',
    title: message,
    toast: true,
    position: 'top',
    showConfirmButton: false,
    timer: 1500,
    timerProgressBar: false,
    width: 'auto',
    customClass: {
      popup: 'small-toast',
      title: 'small-toast-title',
      icon: 'small-toast-icon'
    }
  })
}

const showError = (message) => {
  proxy.$swal.fire({
    icon: 'error',
    title: message,
    toast: true,
    position: 'top',  // 상단 중앙으로 변경
    showConfirmButton: false,
    timer: 1500,  // 1.5초로 조정
    timerProgressBar: false,
    width: 'auto',
    customClass: {
      popup: 'small-toast'
    }
  })
}

const loadComboData = async () => {
  try {
    const response = await dpAPI.getComboData()
    if (response.data.success) {
      const data = response.data.data
      brands.value = data.filter(item => item.GB === 'BR').map(item => ({
        value: item.CD,
        label: item.NM
      }))
      years.value = data.filter(item => item.GB === 'YEAR').map(item => ({
        value: item.CD,
        label: item.NM
      }))
      seasons.value = data.filter(item => item.GB === 'SESN').map(item => ({
        value: item.CD,
        label: item.NM
      }))

      // 콤보 데이터 로드 후 자동으로 조회 실행
      // handleSearch()
    }
  } catch (error) {
    console.error('콤보박스 데이터 로드 실패:', error)
  }
}

const handleSearch = async () => {
  try {
    if (!selectedBrand.value || !selectedYear.value || !selectedSeason.value) {
      alert('브랜드, 연도, 시즌을 모두 선택해주세요.')
      return
    }

    // 현재 선택된 상품이 있다면 수량 저장
    if (selectedProduct.value) {
      try {
        loading.value = true
        await saveQuantities()
        // showSuccess('저장 완료')
      } catch (error) {
        showError('저장 중 오류가 발생했습니다')
        return // 저장 실패시 조회 중단
      } finally {
        loading.value = false
      }
    }

    // 선택된 상품 초기화
    selectedProduct.value = null

    const params = {
      branch_code: authStore.user?.branchCode,
      br_cd: selectedBrand.value,
      year_cd: selectedYear.value,
      sesn_cd: selectedSeason.value
    }

    const response = await dpAPI.getDpList(params)
    
    if (response.data.success) {
      if (response.data.data.length === 0) {
        alert('조회된 데이터가 없습니다.')
        products.value = [] // 목록도 비우기
        return
      }

      products.value = response.data.data.map(item => ({
        ...item,
        sizeQuantities: item.sizes?.reduce((acc, s) => {
          acc[s.SIZE_CD] = s.QTY || 0;
          return acc;
        }, {}) || {},
        originalQuantities: {},
        sizes: item.sizes?.map(s => s.SIZE_CD) || [],
        sizeNames: item.sizes?.reduce((acc, s) => {
          acc[s.SIZE_CD] = s.SIZE_NM;
          return acc;
        }, {}) || {},
        VOTE: item.VOTE,
        evaluationReason: item.RMRK || ''
      }))

      // 아이템 타입별 요약 조회 추가
      await loadItemSummary()
    }
  } catch (error) {
    console.error('DP 목록 조회 실패:', error)
    alert('데이터 조회 중 오류가 발생했습니다.')
    products.value = []
    itemSummary.value = []
  }
}

const filteredProducts = computed(() => {
  if (!products.value) return []
  
  const query = searchQuery.value.toLowerCase()
  return products.value.filter(product => 
    product.STY_NM.toLowerCase().includes(query) ||
    product.COL_NM.toLowerCase().includes(query) ||
    product.DP_CD.toLowerCase().includes(query)
  )
})

onMounted(async () => {
  await loadComboData()
})

// 평가 사유 디바운스 저장 (300ms)
const debouncedSaveReason = debounce(async (reason) => {
  if (selectedProduct.value && !loading.value) {
    try {
      loading.value = true;
      
      await dpAPI.saveVote(selectedProduct.value.DP_CD, {
        shop_cd: authStore.user?.branchCode,
        br_cd: selectedBrand.value,
        year_cd: selectedYear.value,
        sesn_cd: selectedSeason.value,
        dp_cd: selectedProduct.value.DP_CD,
        sty_cd: selectedProduct.value.STY_CD,
        col_cd: selectedProduct.value.COL_CD,
        vote: selectedProduct.value.VOTE,
        rmrk: reason
      });

      // UI 업데이트
      selectedProduct.value.evaluationReason = reason;
      
      // 왼쪽 목록의 제품도 업데이트
      const productIndex = products.value.findIndex(p => p.DP_CD === selectedProduct.value.DP_CD);
      if (productIndex !== -1) {
        products.value[productIndex].RMRK = reason;
      }

    } catch (error) {
      console.error('평가 사유 저장 실패:', error);
    } finally {
      loading.value = false;
    }
  }
}, 300);  // 300ms로 줄임

// 평가 사유 입력 처리
const updateEvaluationReason = (reason) => {
  if (selectedProduct.value) {
    selectedProduct.value.evaluationReason = reason;  // UI 즉시 업데이트
    debouncedSaveReason(reason);  // 디바운스 적용된 저장
  }
};

// 수량 변경
const updateQuantity = async (size, value) => {
  if (selectedProduct.value) {
    // 음수 방지 및 최대값 15 제한
    const newValue = Math.min(Math.max(Number(value) || 0, 0), 15);
    
    // 이전 수량 저장
    const oldValue = selectedProduct.value.sizeQuantities[size] || 0;
    
    // 새 수량 설정
    selectedProduct.value.sizeQuantities[size] = newValue;

    // 왼쪽 목록의 제품도 업데이트
    const productIndex = products.value.findIndex(p => p.DP_CD === selectedProduct.value.DP_CD);
    if (productIndex !== -1) {
      if (!products.value[productIndex].sizeQuantities) {
        products.value[productIndex].sizeQuantities = {};
      }
      products.value[productIndex].sizeQuantities[size] = newValue;
      
      // QTY_SUM 업데이트 - 기존 총합에서 차이만큼만 조정
      const qtyDiff = newValue - oldValue;
      products.value[productIndex].QTY_SUM = (Number(products.value[productIndex].QTY_SUM) || 0) + qtyDiff;
    }

    // 선택된 상품의 QTY_SUM도 같은 방식으로 업데이트
    selectedProduct.value.QTY_SUM = (Number(selectedProduct.value.QTY_SUM) || 0) + (newValue - oldValue);

    // 아이템 타입별 요약 API 호출하여 실시간 업데이트
    const response = await dpAPI.getItemSummary({
      branch_code: authStore.user?.branchCode,
      br_cd: selectedBrand.value,
      year_cd: selectedYear.value,
      sesn_cd: selectedSeason.value
    });
    
    if (response.data.success) {
      itemSummary.value = response.data.data;
    }
  }
};

// 평가 즉시 저장
const updateVote = async (vote) => {
  if (selectedProduct.value && !loading.value) {
    try {
      loading.value = true;
      
      // 서버로 보내는 데이터에 STY_CD, COL_CD 추가
      const saveResponse = await dpAPI.saveVote(selectedProduct.value.DP_CD, {
        shop_cd: authStore.user?.branchCode,
        br_cd: selectedBrand.value,
        year_cd: selectedYear.value,
        sesn_cd: selectedSeason.value,
        dp_cd: selectedProduct.value.DP_CD,
        sty_cd: selectedProduct.value.STY_CD,
        col_cd: selectedProduct.value.COL_CD,
        vote: vote,  // '5' 또는 '1'
        rmrk: selectedProduct.value.evaluationReason || ''
      });

      // 저장 성공 시 바로 UI 업데이트
      const productIndex = products.value.findIndex(p => p.DP_CD === selectedProduct.value.DP_CD);
      if (productIndex !== -1) {
        products.value[productIndex].VOTE = vote;
      }
      selectedProduct.value.VOTE = vote;

      // 저장 성공 로깅
      console.log('평가 저장 성공:', {
        전송한_값: vote,
        응답: saveResponse,
        현재_상태: selectedProduct.value.VOTE,
        상품코드: selectedProduct.value.DP_CD,
        스타일코드: selectedProduct.value.STY_CD,
        컬러코드: selectedProduct.value.COL_CD
      });

    } catch (error) {
      console.error('평가 저장 실패:', error);
    } finally {
      loading.value = false;
    }
  }
};

// 수량 저장
const saveQuantities = async () => {
  if (!selectedProduct.value) return;

  try {
    const changedQuantities = [];
    
    Object.entries(selectedProduct.value.sizeQuantities).forEach(([sizeCode, qty]) => {
      const originalQty = selectedProduct.value.originalQuantities[sizeCode] || 0;
      if (qty !== originalQty) {  // 변경된 수량만 저장
        changedQuantities.push({
          shop_cd: authStore.user?.branchCode,
          br_cd: selectedBrand.value,
          year_cd: selectedYear.value,
          sesn_cd: selectedSeason.value,
          dp_cd: selectedProduct.value.DP_CD,
          sty_cd: selectedProduct.value.STY_CD,
          col_cd: selectedProduct.value.COL_CD,
          size_cd: sizeCode,
          qty: Number(qty) || 0
        });
      }
    });

    if (changedQuantities.length > 0) {
      // 모든 수량 저장이 완료될 때까지 기다립니다
      await Promise.all(changedQuantities.map(data => 
        dpAPI.saveQuantities(selectedProduct.value.DP_CD, data)
      ));

      // 저장 성공 후 원본 수량 업데이트
      selectedProduct.value.originalQuantities = { 
        ...selectedProduct.value.sizeQuantities 
      };

      // 저장 후 상세 정보 다시 조회하여 최신 데이터로 업데이트
      const response = await dpAPI.getDpDetail(selectedProduct.value.DP_CD, {
        branch_code: authStore.user?.branchCode,
        br_cd: selectedBrand.value,
        year_cd: selectedYear.value,
        sesn_cd: selectedSeason.value
      });

      if (response.data?.data?.sizes) {
        const sizes = response.data.data.sizes;
        selectedProduct.value.sizeQuantities = {};
        sizes.forEach(s => {
          selectedProduct.value.sizeQuantities[s.SIZE_CD] = s.QTY || 0;
        });
      }

      // 저장 성공 후 아이템 타입별 요약 다시 조회
      await loadItemSummary();
    }
  } catch (error) {
    console.error('수량 저장 실패:', error);
    throw error;
  }
};

// 상품 선택 시 저장
const selectProduct = async (product) => {
  if (selectedProduct.value) {
    try {
      loading.value = true;
      await saveQuantities();
      // showSuccess('성공적으로 저장되었습니다');
    } catch (error) {
      showError('저장 중 오류가 발생했습니다');
    } finally {
      loading.value = false;
    }
  }
  
  if (selectedProduct.value === product) {
    selectedProduct.value = null;
    return;
  }

  try {
    const response = await dpAPI.getDpDetail(product.DP_CD, {
      branch_code: authStore.user?.branchCode,
      br_cd: selectedBrand.value,
      year_cd: selectedYear.value,
      sesn_cd: selectedSeason.value
    });

    if (!response.data?.data) {
      throw new Error('상세 데이터가 없습니다');
    }

    const { sizes = [] } = response.data.data;
    const updatedProduct = {
      ...product,
      sizeQuantities: sizes.reduce((acc, size) => {
        acc[size.SIZE_CD] = size.QTY || 0;
        return acc;
      }, {}),
      sizes: sizes.map(size => size.SIZE_CD),
      sizeNames: sizes.reduce((acc, size) => {
        acc[size.SIZE_CD] = size.SIZE_NM;
        return acc;
      }, {}),
      VOTE: response.data.data.VOTE,
      evaluationReason: response.data.data.RMRK || ''
    };

    updatedProduct.originalQuantities = { ...updatedProduct.sizeQuantities };
    updatedProduct.QTY_SUM = calculateTotalQuantity(updatedProduct);

    selectedProduct.value = updatedProduct;

  } catch (error) {
    console.error('상세 정보 로드 실패:', error);
    showError('상세 정보 로드 중 오류가 발생했습니다');
  }
};

// 수량 입력 여부 체크
const hasQuantityInput = (product) => {
  return (product.QTY_SUM || 0) > 0;
};

// 총 수량 계산 (모든 곳에서 동일하게 사용)
const calculateTotalQuantity = (product) => {
  if (!product?.sizeQuantities) return 0;
  return Object.values(product.sizeQuantities)
    .reduce((sum, qty) => sum + (Number(qty) || 0), 0);
};

// 아이템 타입별 요약 조회 함수
const loadItemSummary = async () => {
  try {
    const response = await dpAPI.getItemSummary({
      branch_code: authStore.user?.branchCode,
      br_cd: selectedBrand.value,
      year_cd: selectedYear.value,
      sesn_cd: selectedSeason.value
    })
    
    if (response.data.success) {
      itemSummary.value = response.data.data
    }
  } catch (error) {
    console.error('아이템 타입별 요약 조회 실패:', error)
  }
}

// 저장하기 버튼 클릭
const handleSave = async () => {
  if (loading.value) return
  
  try {
    loading.value = true
    await saveQuantities()
    // 저장 성공 후 아이템 타입별 요약 다시 조회
    await loadItemSummary()
    showSuccess('저장 완료')
  } catch (error) {
    showError('저장 중 오류가 발생했습니다')
  } finally {
    loading.value = false
  }
}

onBeforeUnmount(async () => {
  debouncedSaveReason.flush();  // 대기 중인 평가 사유 저장
  await saveQuantities();
});
</script>

<style scoped>
.color-badge {
  padding: 0px 4px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  display: inline-block;
  white-space: nowrap;
  transform: scale(0.9);
  margin-left: 4px;
}

@media (min-width: 640px) {
  .color-badge {
    padding: 1px 6px;
    border-radius: 6px;
    font-size: 11px;
  }
}

/* 기본 색상 */
.badge-white,
.badge-o\/white {
  background-color: #FFFFFF;
  color: #333333;
  border: 1px solid #E0E0E0;
}

.badge-blue {
  background-color: rgb(191, 219, 254);
  color: #023e89;
}

.badge-l\/blue {
  background-color: rgb(219, 234, 254);
  color: #1e40af;
}

.badge-ivory {
  background-color: #FFFFF0;
  color: #8B7355;
  border: 1px solid #E0E0E0;
}

.badge-pink {
  background-color: #FFE4E1;
  color: #FF1493;
}

.badge-c\/pink {
  background-color: #FFE4E8;
  color: #DB7093;
}

.badge-green {
  background-color: rgb(212, 237, 218);
  color: #2a6538;
}

.badge-yellow {
  background-color: rgb(255, 251, 204);
  color: #7a6600;
}

.badge-khaki {
  background-color: #F0E68C;
  color: #8B864E;
}

.badge-brown {
  background-color: #DEB887;
  color: #8B4513;
}

.badge-orange {
  background-color: #FFE4B5;
  color: #FF8C00;
}

.badge-cream {
  background-color: #FFFDD0;
  color: #8B8B83;
  border: 1px solid #E0E0E0;
}

.badge-purple {
  background-color: rgb(233, 213, 255);
  color: #5b0099;
}

.badge-mint {
  background-color: #E0FFFF;
  color: #48D1CC;
}

.badge-red {
  background-color: rgb(255, 204, 204);
  color: #990000;
}

.badge-beige {
  background-color: #F5F5DC;
  color: #8B8B83;
  border: 1px solid #E0E0E0;
}

.badge-grey {
  background-color: #D3D3D3;
  color: #696969;
}

.badge-navy {
  background-color: #000080;
  color: #FFFFFF;
}

/* 기본값 */
.badge-default {
  background-color: #e0e0e0;
  color: #333333;
}

/* 모바일에서 더블탭 줌 방지 */
* {
  touch-action: manipulation;
}

/* 숫자 입력 필드의 스피너 버튼 제거 */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}

/* SweetAlert2 커스텀 스타일 */
.small-toast {
  padding: 0.5rem 1rem !important;
  font-size: 0.875rem !important;
  min-height: 2rem !important;
  display: flex !important;
  align-items: center !important;
  background-color: #4CAF50 !important;
  color: white !important;
  border-radius: 4px !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

.small-toast-title {
  margin: 0 !important;
  font-weight: 500 !important;
  font-size: 0.875rem !important;
}

.small-toast-icon {
  margin: 0 0.5rem 0 0 !important;
  font-size: 0.875rem !important;
  width: 1.25em !important;
  height: 1.25em !important;
}

/* 모바일 환경에서 더 작게 조정 */
@media (max-width: 640px) {
  .small-toast {
    padding: 0.375rem 0.75rem !important;
    font-size: 0.75rem !important;
    min-height: 1.75rem !important;
  }

  .small-toast-title {
    font-size: 0.75rem !important;
  }

  .small-toast-icon {
    font-size: 0.75rem !important;
    width: 1.1em !important;
    height: 1.1em !important;
    margin: 0 0.375rem 0 0 !important;
  }
}

.badge-l\/beige {
  background-color: #F8F6F0;
  color: #8B8B83;
  border: 1px solid #E0E0E0;
}

.badge-l\/khaki {
  background-color: #F4E8C1;
  color: #8B7355;
}

.badge-melan {
  background-color: #A9A9A9;
  color: #FFFFFF;
}

.badge-l\/orange {
  background-color: #FFE5CC;
  color: #CC5500;
}

.badge-d\/blue {
  background-color: #00008B;
  color: #FFFFFF;
}

.badge-l\/grey {
  background-color: #E8E8E8;
  color: #666666;
}

.badge-charcoal {
  background-color: #36454F;
  color: #FFFFFF;
}

.badge-black {
  background-color: #000000;
  color: #FFFFFF;
}
</style>